import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Send } from 'lucide-react';
import { toast } from 'sonner';
import { ANIMAL_ICONS } from '../theme';
import { useHistoryClose } from '../hooks/useHistoryClose';
import { ChatUpgradeModal } from './premium/ChatUpgradeModal';
import { getEntitlement } from '../utils/billing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/global-chat`;
const ROOM_STYLE = {
  dragon:  { accent: '#E4A62A', soft: '#F5C860' }, fashion: { accent: '#D4AF37', soft: '#F5D96A' },
  food:    { accent: '#C8A95E', soft: '#E8CE94' }, explore: { accent: '#0F52BA', soft: '#4A85E8' },
  souls:   { accent: '#DC143C', soft: '#FF4A6A' }, sporty:  { accent: '#10B981', soft: '#4FE8B4' },
  success: { accent: '#E5C44D', soft: '#F5DE85' }, legacy:  { accent: '#8B5CF6', soft: '#B08CFF' },
  art:     { accent: '#2A9BD8', soft: '#5BC0F0' },
};
const POLL_MS = 3000;

const Icon = ({ id, size, ring, dim }) => {
  const ic = ANIMAL_ICONS[id] || ANIMAL_ICONS.dragon;
  return (
    <span className="rounded-full overflow-hidden flex-shrink-0 inline-block"
      style={{ width: size, height: size, border: `1.5px solid ${ring}`, boxShadow: dim ? 'none' : `0 0 10px ${ring}88`,
        filter: dim ? 'grayscale(1) brightness(0.35)' : 'none', transition: 'filter 0.4s' }}>
      <img src={ic.src} alt="" className="w-full h-full object-cover" style={{ objectPosition: ic.pos }}/>
    </span>
  );
};

const Countdown = ({ endsAt, color }) => {
  const [left, setLeft] = useState('');
  useEffect(() => {
    const tick = () => {
      const ms = Math.max(0, new Date(endsAt).getTime() - Date.now());
      setLeft(`${String(Math.floor(ms / 60000)).padStart(2, '0')}:${String(Math.floor(ms / 1000) % 60).padStart(2, '0')}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  return <span className="font-['Outfit'] tabular-nums" style={{ color }} data-testid="gchat-countdown">{left}</span>;
};

const Coins = ({ coins }) => (
  <div className="flex items-center justify-center gap-1.5 flex-wrap" data-testid="gchat-coins">
    {(coins?.order || []).map((id, i) => {
      const s = ROOM_STYLE[id];
      return <Icon key={i} id={id} size={22} ring={s.accent} dim={i < (coins.used || 0)}/>;
    })}
    <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.16em] ml-1 text-white/60" data-testid="gchat-coins-left">
      {coins?.left ?? 0}/{coins?.total ?? 10}
    </span>
  </div>
);

const Message = ({ m }) => {
  const s = ROOM_STYLE[m.profile_tag] || ROOM_STYLE.dragon;
  return (
    <motion.div layout initial={{ opacity: 0, y: 28, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }}
      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      className="flex items-start gap-2 py-1" data-testid={`gchat-msg-${m.id}`}>
      {m.profile_picture
        ? <img src={m.profile_picture} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" style={{ border: `1.5px solid ${s.accent}`, boxShadow: `0 0 8px ${s.accent}88` }}/>
        : <Icon id={m.profile_tag || 'dragon'} size={32} ring={s.accent}/>}
      <div className="min-w-0 rounded-2xl rounded-tl-sm px-3 py-1.5"
        style={{ background: `linear-gradient(120deg, ${s.accent}2E, rgba(10,10,16,0.85) 70%)`, borderLeft: `2px solid ${s.accent}`,
          boxShadow: `0 2px 10px rgba(0,0,0,0.4), inset 0 0 0 1px ${s.accent}22` }}>
        <span className="block font-['Outfit'] text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: s.soft }}>{m.nickname}</span>
        <p className="font-['Outfit'] text-[13.5px] leading-snug text-white/92 break-words">{m.text}</p>
      </div>
    </motion.div>
  );
};

const RING = ['fashion', 'food', 'explore', 'souls', 'sporty', 'success', 'legacy', 'art'];
const FRAME = `conic-gradient(from 0deg, ${RING.map((k) => ROOM_STYLE[k].accent).join(', ')}, ${ROOM_STYLE.fashion.accent})`;

const CrestBar = ({ roomId }) => (
  <div className="absolute left-1/2 -translate-x-1/2 -top-3.5 z-10 flex items-center gap-1 px-2 py-0.5 rounded-full"
    style={{ background: '#05050A', border: '1px solid rgba(255,255,255,0.12)' }} data-testid="gchat-crest">
    {['dragon', ...RING].map((k) => (
      <span key={k} style={{ transform: k === roomId ? 'scale(1.25)' : 'none', transition: 'transform 0.4s' }}>
        <Icon id={k} size={k === roomId ? 22 : 18} ring={ROOM_STYLE[k].accent} dim={k !== roomId}/>
      </span>
    ))}
  </div>
);

/* Global Bliss Chat — one live room per hour, cycling the 9 icons; past-hour messages are deleted server-side. */
export const GlobalBlissChat = ({ onClose }) => {
  useHistoryClose(true, onClose);
  const [room, setRoom] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [coins, setCoins] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [upsell, setUpsell] = useState(false);
  const lastRef = useRef('');
  const hourRef = useRef('');
  const listRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await axios.get(API, { params: { after: lastRef.current } });
      if (data.room.hour_key !== hourRef.current) {
        hourRef.current = data.room.hour_key;
        lastRef.current = '';
        setRoom(data.room);
        const fresh = await axios.get(API);
        setMsgs(fresh.data.messages);
        if (fresh.data.messages.length) lastRef.current = fresh.data.messages[fresh.data.messages.length - 1].created_at;
        setCoins(fresh.data.coins);
        return;
      }
      setCoins(data.coins);
      if (data.messages.length) {
        lastRef.current = data.messages[data.messages.length - 1].created_at;
        setMsgs((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          return [...prev, ...data.messages.filter((m) => !seen.has(m.id))].slice(-300);
        });
      }
    } catch (_) { /* network blip; next poll retries */ }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(() => { if (!document.hidden) poll(); }, POLL_MS);
    return () => clearInterval(t);
  }, [poll]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [msgs.length]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    const ent = await getEntitlement(true);
    if (ent.paywall_enabled && !ent.chat_entitled) { setUpsell(true); return; }
    setSending(true);
    try {
      const { data } = await axios.post(API, { text: t });
      setText('');
      setCoins(data.coins);
      lastRef.current = data.message.created_at;
      setMsgs((prev) => (prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]));
    } catch (e) {
      if (e?.response?.status === 402) setUpsell(true);
      else toast(e?.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  const s = ROOM_STYLE[room?.id] || ROOM_STYLE.dragon;
  const out = coins && coins.left <= 0;

  return createPortal(
    <div className="fixed inset-0 z-[300] flex flex-col bg-black" data-testid="gchat-page"
      style={{ background: `radial-gradient(ellipse at 50% 0%, ${s.accent}30, #000 60%), #000`,
        paddingTop: 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
        paddingBottom: 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))' }}>
      <style>{`@keyframes gchatBreath{0%,88%,100%{box-shadow:0 0 14px var(--gcg)}94%{box-shadow:0 0 38px var(--gc),0 0 70px var(--gcg)}}`}</style>
      <div className="relative z-20 flex items-center px-3 pt-2 flex-shrink-0">
        <button type="button" onClick={onClose} data-testid="gchat-back"
          className="h-8 px-3 rounded-full flex items-center gap-1 active:scale-95"
          style={{ border: `1px solid ${s.accent}8C`, background: `${s.accent}14`, color: s.soft }}>
          <ChevronLeft size={16}/><span className="font-['Outfit'] uppercase" style={{ fontSize: 11, letterSpacing: '0.18em' }}>Back</span>
        </button>
      </div>

      <div className="flex flex-col items-center -mt-6 flex-shrink-0">
        <AnimatePresence mode="wait">
          <motion.div key={room?.hour_key || 'x'} initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.2 }}
            transition={{ duration: 0.6 }}>
            <Icon id={room?.id || 'dragon'} size={64} ring={s.accent}/>
          </motion.div>
        </AnimatePresence>
        <p className="font-['Cinzel'] uppercase mt-1.5" style={{ fontSize: 16, letterSpacing: '0.3em', color: s.soft, textShadow: `0 0 14px ${s.accent}` }}
          data-testid="gchat-room-title">{room ? `${room.label} Hour` : 'Global Bliss Chat'}</p>
        {room && (
          <p className="font-['Cormorant_Garamond'] italic text-white/60 text-[13px]" data-testid="gchat-room-sub">
            Vanishes in <Countdown endsAt={room.ends_at} color={s.soft}/> · next {room.next}
          </p>
        )}
        <div className="mt-2"><Coins coins={coins}/></div>
      </div>

      <div className="relative flex-1 min-h-0 mx-3 mt-7 rounded-[22px] p-[2px]" data-testid="gchat-window"
        style={{ '--gc': s.accent, '--gcg': `${s.accent}33`, background: FRAME, animation: 'gchatBreath 60s ease-in-out infinite' }}>
        <CrestBar roomId={room?.id}/>
        <div className="relative w-full h-full rounded-[21px] flex flex-col overflow-hidden"
          style={{ background: `radial-gradient(ellipse at 50% 0%, ${s.accent}22, rgba(4,4,8,0.97) 55%), #040408` }}>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ opacity: 0.07 }}>
            <Icon id={room?.id || 'dragon'} size={220} ring="transparent"/>
          </div>
          <div className="relative px-3 pt-4 pb-1.5 font-['Outfit'] uppercase text-[9px] tracking-[0.24em] flex items-center justify-between flex-shrink-0"
            style={{ color: s.soft, borderBottom: `1px solid ${s.accent}33` }}>
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#FF4A6A', boxShadow: '0 0 6px #FF4A6A' }}/> Live · Global</span>
            <span className="text-white/45" data-testid="gchat-msg-count">{msgs.length} voices</span>
          </div>
          <div ref={listRef} className="relative flex-1 min-h-0 overflow-y-auto px-3 py-2" data-testid="gchat-list">
            {!msgs.length && (
              <p className="text-center font-['Cormorant_Garamond'] italic text-white/50 mt-10 text-[15px]" data-testid="gchat-empty">
                The {room?.label || 'Empire'} hour is quiet. Be the first voice.
              </p>
            )}
            <AnimatePresence initial={false}>{msgs.map((m) => <Message key={m.id} m={m}/>)}</AnimatePresence>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 px-3 py-2.5 flex-shrink-0">
        <div className="flex-1 flex items-center h-11 rounded-full px-4" style={{ border: `1px solid ${s.accent}80`, background: `${s.accent}10` }}>
          <input value={text} onChange={(e) => setText(e.target.value.slice(0, 200))} disabled={out}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            placeholder={out ? 'Coins refill at midnight IST' : `Chat in the ${room?.label || ''} hour…`}
            className="flex-1 min-w-0 bg-transparent outline-none font-['Outfit'] text-[14px] text-white placeholder-white/35"
            data-testid="gchat-input"/>
          <span className="font-['Outfit'] text-[9px] text-white/40 tabular-nums ml-2">{text.length}/200</span>
        </div>
        <button type="button" onClick={send} disabled={out || sending || !text.trim()} data-testid="gchat-send"
          className="w-11 h-11 rounded-full flex items-center justify-center active:scale-90 disabled:opacity-40"
          style={{ background: `linear-gradient(135deg, ${s.soft}, ${s.accent})`, boxShadow: `0 0 14px ${s.accent}88` }}>
          <Send size={17} color="#0a0806"/>
        </button>
      </div>
      {upsell && <ChatUpgradeModal onClose={() => setUpsell(false)}/>}
    </div>,
    document.getElementById('root') || document.body
  );
};

export default GlobalBlissChat;
